import { useEffect, useState } from 'react'
import { faSeedling } from '@fortawesome/pro-solid-svg-icons'

import { getCurrentUser } from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { formatMonthRange } from '@/utils/dateHelpers'
import { logHeardError } from '@/utils/errorHelpers'
import { Card, CardColors, Icon, ListCustomBullets, Loader, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { useAnalyticsView } from '@/features/amplitude'
import {
  HAVEN_ENTRY_POINT,
  type CatchUpWindow,
} from '@/features/adviser-chat/lib/types'
import { useLaunchHavenCubk } from '@/features/adviser-chat/hooks/useLaunchHavenCubk'
import { fetchCatchUpWindow } from '@/features/adviser-chat/adviser-chat.actions'
import { OnboardingStickyNav } from '@/features/onboarding/shared/OnboardingStickyNav'
import { useFixedFooterOffset } from '@/features/onboarding/shared/useFixedFooterOffset'

const monthsLabel = (count: number) => (count === 1 ? 'month' : 'months')

const Step = ({
  heading,
  duration,
  description,
}: {
  heading: string
  duration: string
  description: string
}) => (
  <ListCustomBullets.Item
    numberBgColor="bg-moss-50"
    numberTextColor="text-moss-300"
    className="items-start max-sm:flex-col max-sm:gap-2"
  >
    <div className="grid grid-cols-1 gap-y-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:gap-x-4">
      <Text
        variant={TypographyStyles.LABEL_MD}
        className="sm:col-start-1 sm:row-start-1"
      >
        {heading}
      </Text>
      <Text
        variant={TypographyStyles.BODY_XS}
        color={TextColors.MUTED}
        className="whitespace-nowrap sm:col-start-2 sm:row-start-1 sm:pt-0.5"
      >
        {duration}
      </Text>
      <Text
        variant={TypographyStyles.BODY_SM}
        color={TextColors.MUTED}
        className="sm:col-start-1 sm:row-start-2"
      >
        {description}
      </Text>
    </div>
  </ListCustomBullets.Item>
)

export const HavenCubkLanding = () => {
  const currentUser = useReselector(getCurrentUser)
  const pageView = useAnalyticsView()
  const launchHaven = useLaunchHavenCubk(
    HAVEN_ENTRY_POINT.onboardingSetupLanding
  )
  const footerLeftOffset = useFixedFooterOffset()
  const [catchUpWindow, setCatchUpWindow] = useState<CatchUpWindow | null>(null)

  useEffect(() => {
    pageView('onboarding haven cubk landing')
  }, [pageView])

  useEffect(() => {
    let cancelled = false
    fetchCatchUpWindow()
      .then((window) => {
        if (!cancelled) setCatchUpWindow(window)
      })
      .catch((err) => {
        logHeardError(err, {
          context: 'haven cubk landing: fetch catch-up window',
        })
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (!currentUser) return <Loader active />

  const reachBack = catchUpWindow
    ? `${catchUpWindow.totalMonths} ${monthsLabel(catchUpWindow.totalMonths)}`
    : 'far enough'
  const gapsDescription = `Banks don't always reach back ${reachBack}. We'll name the months we need and you drop those statements in.`

  return (
    <div className="flex flex-col min-h-full">
      <div className="flex-1 w-full max-w-200 mx-auto px-4 pt-6 pb-[96px] max-sm:pb-6">
        <Text variant={TypographyStyles.EYEBROW} className="text-neutral-400">
          Welcome to Heard, {currentUser.firstName}
        </Text>
        <Text variant={TypographyStyles.DISPLAY_SM} className="mt-2">
          Let&apos;s get you set up.
        </Text>
        <Text
          variant={TypographyStyles.BODY_LG}
          color={TextColors.MUTED}
          className="mt-3 max-w-170"
        >
          Connect your accounts, answer a few questions about your transactions,
          and Haven gets your books caught up.
        </Text>

        {catchUpWindow && (
          <div className="mt-10 flex items-start gap-4">
            <Text variant={TypographyStyles.DISPLAY_LG}>
              {catchUpWindow.totalMonths}
            </Text>
            <div>
              <Text variant={TypographyStyles.DISPLAY_XXS}>
                {monthsLabel(catchUpWindow.totalMonths)} to catch up
              </Text>
              <Text variant={TypographyStyles.BODY_MD} color={TextColors.MUTED}>
                {formatMonthRange(
                  catchUpWindow.firstMonth,
                  catchUpWindow.lastMonth
                )}
              </Text>
            </div>
          </div>
        )}

        <ListCustomBullets variant="number" className="mt-10 sm:pl-5">
          <Step
            heading="Connect your banks"
            duration="A few minutes"
            description="We pull in your transactions and categorize them automatically."
          />
          <Step
            heading="Fill the gaps"
            duration="About 2 minutes"
            description={gapsDescription}
          />
          <Step
            heading="Confirm your write-offs"
            duration="A few minutes at a time"
            description="We categorize everything and only ask about the ones nobody could work out."
          />
        </ListCustomBullets>

        <Card className="mt-10" color={CardColors.EMPHASIS}>
          <header className="inline-flex items-center">
            <Icon
              icon={faSeedling}
              className="mr-2 h-4 w-4"
              backgroundStyle="circle"
            />
            <Text variant={TypographyStyles.H3}>
              Haven walks you through it
            </Text>
          </header>
          <Text
            variant={TypographyStyles.BODY_SM}
            color={TextColors.MUTED}
            className="pt-4"
          >
            Haven is Heard&rsquo;s assistant. It&rsquo;ll get your books up to
            date and only ask when it&rsquo;s unsure. Need a hand? Press
            &ldquo;I&rsquo;m not sure.&rdquo; Haven helps first, and you can
            always ask for a bookkeeper, who&rsquo;ll usually reply within a day
            or two.
          </Text>
        </Card>
      </div>

      <OnboardingStickyNav
        leftOffset={footerLeftOffset}
        className="max-w-200 px-4"
        leading={
          <Text
            variant={TypographyStyles.BODY_MD}
            color={TextColors.MUTED}
            className="max-sm:text-center"
          >
            Around 5 minutes in all.
          </Text>
        }
        onContinue={launchHaven}
        continueLabel="Get started"
        continueIcon={null}
      />
    </div>
  )
}
